import { object, string, number, preprocess, literal, array, boolean } from 'zod'
import { EMPTY_STRING, HASWORDS_PARTIAL, NUMBER_OPERATOR_INPUT_OPTIONS, ALPHANUMERIC_INPUT_OPTIONS } from './regexp'
import { SearchOperators } from './enums'

const hasWordsRegex = new RegExp(HASWORDS_PARTIAL, 'i')
const numberWithOperatorRegex = new RegExp([NUMBER_OPERATOR_INPUT_OPTIONS, EMPTY_STRING].join('|'), 'i')
const alphaNumRegex = new RegExp([ALPHANUMERIC_INPUT_OPTIONS, EMPTY_STRING].join('|'), 'i')

// UTILS
const isLowValueRequired = (operator: string) =>
    !!operator &&
    [
        SearchOperators.BETWEEN,
        SearchOperators.EQUAL_TO,
        SearchOperators.GREATER_THAN,
        SearchOperators.LESS_THAN,
        SearchOperators.NOT_EQUAL_TO,
    ].includes(operator as SearchOperators)
const isHighValueRequired = (operator: string) =>
    !!operator && [SearchOperators.BETWEEN].includes(operator as SearchOperators)

// REFINEMENTS
const refineLowNumberRequired = (operator: string, value: string): boolean =>
    isLowValueRequired(operator) ? +value >= 0 : true
const refineLowValueRequired = (operator: string, value: string): boolean =>
    isLowValueRequired(operator) ? !!value : true
const refineHighNumberRequired = (operator: string, value: string): boolean =>
    isHighValueRequired(operator) ? +value >= 1 : true
const refineHighGtLow = (low: string, high: string): boolean => {
    const lowAsNumber = +low
    const highAsNumber = +high
    return lowAsNumber && highAsNumber ? lowAsNumber < highAsNumber : true
}
const workflowAllowedViews = ['vehicles', 'vehicles-buyers', 'vehicles-sellers', 'vehicles-deals']
const refineWorkflowViews = (searchDetailView: string, workflows?: (boolean | undefined)[]): boolean => {
    // Edit Sale Conditions Workflow not allowed on some views
    if (workflows?.[0] === true) {
        const isAllowed = workflowAllowedViews.includes(searchDetailView)
        return isAllowed
    }
    return true
}

// PROCESSORS
const trimmedStringNoQuotesProcessor = (v: unknown) =>
    String(v || '')
        .trim()
        .replace(/\s\s+/g, ' ')
        .replace(/"|'/g, '')
const trimmedStringAsNumberProcessor = (v: unknown) => Number(String(v || '').trim())

// COMMON SCHEMAS
const commonIntegerSchema = () =>
    preprocess(
        trimmedStringAsNumberProcessor,
        number({ invalid_type_error: 'Must be a number' })
            .int()
            .nonnegative()
            .transform((v) => (v || v === 0 ? String(v) : '')),
    )
const yearSchema = () =>
    preprocess(
        trimmedStringAsNumberProcessor,
        number({ invalid_type_error: 'Must be a number' })
            .int()
            .gte(1900)
            .lte(2099)
            .or(literal(0))
            .transform((v) => (v ? String(v) : '')),
    )
const hasWordsSchema = () => preprocess(trimmedStringNoQuotesProcessor, string().regex(hasWordsRegex))
const commonAlphaNumericFieldSchema = () => preprocess(trimmedStringNoQuotesProcessor, string().regex(alphaNumRegex))
const commonNumberOperatorFieldSchema = () =>
    preprocess(trimmedStringNoQuotesProcessor, string().regex(numberWithOperatorRegex))
const commonTrimmedStringNoQuotesSchema = () => preprocess(trimmedStringNoQuotesProcessor, string())

// export const yupSchema: ObjectSchema<DashboardCardConfig> = object({
export const zodSchema = object({
    aql: string(),
    format: object({
        cardTitle: commonTrimmedStringNoQuotesSchema(),
        firstPositionDataField: string(),
        secondPositionDataField: string(),
        mainLabel: commonTrimmedStringNoQuotesSchema(),
        mainLabel2: commonTrimmedStringNoQuotesSchema(),
        mainSubLabel: commonTrimmedStringNoQuotesSchema(),
        primaryDataField: string(),
        searchDetailTitle: commonTrimmedStringNoQuotesSchema(),
        searchDetailView: string(),
        cardActionLabel: string(),
        cardActionUrl: string(),
    }),
    filters: object({
        hasWords: hasWordsSchema(),
        company: object({
            buyer: commonAlphaNumericFieldSchema(),
            isFavorite: string(),
            seller: commonAlphaNumericFieldSchema(),
        })
            .and(
                object({ rep: commonAlphaNumericFieldSchema(), repOp: string(), repType: string() }).refine(
                    (data) => refineLowValueRequired(data.repOp, data.rep),
                    {
                        message: 'Required',
                        path: ['rep'],
                    },
                ),
            )
            .and(
                object({
                    buyerId: commonAlphaNumericFieldSchema(),
                    buyerIdOp: string(),
                }).refine((data) => refineLowValueRequired(data.buyerIdOp, data.buyerId), {
                    message: 'Required',
                    path: ['buyerId'],
                }),
            )
            .and(
                object({
                    buyerType: commonAlphaNumericFieldSchema(),
                    buyerTypeOp: string(),
                }).refine((data) => refineLowValueRequired(data.buyerTypeOp, data.buyerType), {
                    message: 'Required',
                    path: ['buyerType'],
                }),
            )
            .and(
                object({
                    sellerId: commonAlphaNumericFieldSchema(),
                    sellerIdOp: string(),
                }).refine((data) => refineLowValueRequired(data.sellerIdOp, data.sellerId), {
                    message: 'Required',
                    path: ['sellerId'],
                }),
            )
            .and(
                object({ sellerType: commonAlphaNumericFieldSchema(), sellerTypeOp: string() }).refine(
                    (data) => refineLowValueRequired(data.sellerTypeOp, data.sellerType),
                    {
                        message: 'Required',
                        path: ['sellerType'],
                    },
                ),
            ),

        contact: object({
            completedAt: commonNumberOperatorFieldSchema(),
            completedFullName: commonAlphaNumericFieldSchema(),
            contactMethod: string(),
            contactStatus: string(),
            contactedAt: commonNumberOperatorFieldSchema(),
            creatorFullName: commonAlphaNumericFieldSchema(),
            followupAt: commonNumberOperatorFieldSchema(),
            followupFullName: commonAlphaNumericFieldSchema(),
        }),
        sale: object({
            lane: commonAlphaNumericFieldSchema(),
            run: commonAlphaNumericFieldSchema(),
            saleDate: commonNumberOperatorFieldSchema(),
            saleStatus: commonAlphaNumericFieldSchema(),
        })
            .and(
                object({
                    blackbook: string(),
                    blackbookHigh: commonIntegerSchema(),
                    blackbookLow: commonIntegerSchema(),
                })
                    .refine((data) => refineLowNumberRequired(data.blackbook, data.blackbookLow), {
                        message: 'Required',
                        path: ['blackbookLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.blackbook, data.blackbookHigh), {
                        message: 'Required',
                        path: ['blackbookHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.blackbookLow, data.blackbookHigh), {
                        message: 'Must be higher than low value',
                        path: ['blackbookHigh'],
                    }),
            )
            .and(
                object({ floor: string(), floorHigh: commonIntegerSchema(), floorLow: commonIntegerSchema() })
                    .refine((data) => refineLowNumberRequired(data.floor, data.floorLow), {
                        message: 'Required',
                        path: ['floorLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.floor, data.floorHigh), {
                        message: 'Required',
                        path: ['floorHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.floorLow, data.floorHigh), {
                        message: 'Must be higher than low value',
                        path: ['floorHigh'],
                    }),
            )
            .and(
                object({ lot: string(), lotHigh: commonIntegerSchema(), lotLow: commonIntegerSchema() })
                    .refine((data) => refineLowNumberRequired(data.lot, data.lotLow), {
                        message: 'Required',
                        path: ['lotLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.lot, data.lotHigh), {
                        message: 'Required',
                        path: ['lotHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.lotLow, data.lotHigh), {
                        message: 'Must be higher than low value',
                        path: ['lotHigh'],
                    }),
            )
            .and(
                object({ mmr: string(), mmrHigh: commonIntegerSchema(), mmrLow: commonIntegerSchema() })
                    .refine((data) => refineLowNumberRequired(data.mmr, data.mmrLow), {
                        message: 'Required',
                        path: ['mmrLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.mmr, data.mmrHigh), {
                        message: 'Required',
                        path: ['mmrHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.mmrLow, data.mmrHigh), {
                        message: 'Must be higher than low value',
                        path: ['mmrHigh'],
                    }),
            )
            .and(
                object({ ranCount: string(), ranCountHigh: commonIntegerSchema(), ranCountLow: commonIntegerSchema() })
                    .refine((data) => refineLowNumberRequired(data.ranCount, data.ranCountLow), {
                        message: 'Required',
                        path: ['ranCountLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.ranCount, data.ranCountHigh), {
                        message: 'Required',
                        path: ['ranCountHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.ranCountLow, data.ranCountHigh), {
                        message: 'Must be higher than low value',
                        path: ['ranCountHigh'],
                    }),
            ),

        vehicle: object({
            checkIn: commonNumberOperatorFieldSchema(),
            color: commonAlphaNumericFieldSchema(),
            hasCr: string(),
            hasTitle: string(),
            make: commonAlphaNumericFieldSchema(),
            model: commonAlphaNumericFieldSchema(),
        })
            .and(
                object({
                    photos: string(),
                    photosHigh: commonIntegerSchema(),
                    photosLow: commonIntegerSchema(),
                })
                    .refine((data) => refineLowNumberRequired(data.photos, data.photosLow), {
                        message: 'Required',
                        path: ['photosLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.photos, data.photosHigh), {
                        message: 'Required',
                        path: ['photosHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.photosLow, data.photosHigh), {
                        message: 'Must be higher than low value',
                        path: ['photosHigh'],
                    }),
            )
            .and(
                object({
                    odo: string(),
                    odoHigh: commonIntegerSchema(),
                    odoLow: commonIntegerSchema(),
                })
                    .refine((data) => refineLowNumberRequired(data.odo, data.odoLow), {
                        message: 'Required',
                        path: ['odoLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.odo, data.odoHigh), {
                        message: 'Required',
                        path: ['odoHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.odoLow, data.odoHigh), {
                        message: 'Must be higher than low value',
                        path: ['odoHigh'],
                    }),
            )
            .and(
                object({
                    year: string(),
                    yearHigh: yearSchema(),
                    yearLow: yearSchema(),
                })
                    .refine((data) => refineLowNumberRequired(data.year, data.yearLow), {
                        message: 'Required',
                        path: ['yearLow'],
                    })
                    .refine((data) => refineHighNumberRequired(data.year, data.yearHigh), {
                        message: 'Required',
                        path: ['yearHigh'],
                    })
                    .refine((data) => refineHighGtLow(data.yearLow, data.yearHigh), {
                        message: 'Must be higher than low value',
                        path: ['yearHigh'],
                    }),
            ),
    }),
    type: string(),
    workflows: array(boolean().optional()).optional(),
}).refine((schema) => refineWorkflowViews(schema.format.searchDetailView, schema.workflows), {
    message: 'Workflows not allowed on this view',
    path: ['workflows'],
})

export default zodSchema
